import { useState, useEffect, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { DocumentPreviewOverlay } from './DocumentPreviewOverlay';
import { Loader2, CreditCard, Building2, Lock, Check, AlertCircle, Download, PenLine, ThumbsDown, Clock } from 'lucide-react';
import { publicClientApi, publicDocumentApi } from '@/lib/api';
import { StripePaymentForm } from '@/components/StripePaymentForm';
import { ClientSignatureModal } from '@/components/Modals/ClientSignatureModal';
import { StarRating } from '@/components/ui/star-rating';
import { formatAmount } from '@/lib/currency';
import { DocumentSummary } from './DocumentSummary';
import type { PublicBusinessContactInfo } from '@/lib/api';

type PaymentMethod = 'card' | 'ach';

interface CheckoutLayoutProps {
  documentType: 'invoice' | 'quote';
  documentId: string;
  clientId: string;
  business: PublicBusinessContactInfo;
  stripeAccountId: string;
  // Document details for summary
  documentNumber: string;
  documentDate: string | null;
  dueDate?: string | null;
  subtotal: number;
  shippingFee?: number;
  totalTax: number;
  totalAmount: number;
  // For invoices
  amountPaid?: number;
  balanceDue?: number;
  // For quotes
  depositAmount?: number;
  // Payment config
  cardEnabled: boolean;
  achEnabled: boolean;
  availableCredit: number;
  // Client info
  clientName: string;
  clientCompany?: string;
  clientAddress?: string;
  // Signature flow
  requiresSignature?: boolean;
  signatureCollected?: boolean;
  contractContent?: string;
  onSignComplete?: (name: string, image: string) => Promise<void>;
  // Callbacks
  onPaymentSuccess: () => void;
  onDecline?: () => void;
  declining?: boolean;
  // Full document view
  fullDocumentContent: ReactNode;
  // PDF download - either a URL or a callback for on-demand generation
  pdfUrl?: string | null;
  onDownloadPdf?: () => Promise<Blob>;
  pdfFilename?: string;
  // Client reviews
  clientReviewsEnabled?: boolean;
}

export function CheckoutLayout({
  documentType,
  documentId,
  clientId,
  business,
  stripeAccountId,
  documentNumber,
  documentDate,
  dueDate,
  subtotal,
  shippingFee = 0,
  totalTax,
  totalAmount,
  amountPaid = 0,
  balanceDue = 0,
  depositAmount = 0,
  cardEnabled,
  achEnabled,
  availableCredit,
  clientName,
  clientCompany,
  clientAddress,
  requiresSignature = false,
  signatureCollected = false,
  contractContent,
  onSignComplete,
  onPaymentSuccess,
  onDecline,
  declining = false,
  fullDocumentContent,
  pdfUrl,
  onDownloadPdf,
  pdfFilename,
  clientReviewsEnabled = false,
}: CheckoutLayoutProps) {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(cardEnabled ? 'card' : 'ach');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Pending payment state
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(true);
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  const [pendingPaymentAmount, setPendingPaymentAmount] = useState(0);
  const [paymentPending, setPaymentPending] = useState(false); // Set when ACH payment is submitted
  const [fetchedStripeAccountId, setFetchedStripeAccountId] = useState('');

  // Fetch payment info to check for pending payments and get stripe account ID
  useEffect(() => {
    const fetchPaymentInfo = async () => {
      const getPaymentInfo = documentType === 'quote'
        ? publicClientApi.getQuotePaymentInfo
        : publicClientApi.getInvoicePaymentInfo;

      const response = await getPaymentInfo(clientId, documentId);

      if (response.data) {
        setHasPendingPayment(response.data.has_pending_payment);
        setPendingPaymentAmount(parseFloat(response.data.pending_payment_amount));
        setFetchedStripeAccountId(response.data.stripe_account_id);
      }
      setLoadingPaymentInfo(false);
    };

    fetchPaymentInfo();
  }, [clientId, documentId, documentType]);

  // Use fetched stripe account ID (from payment info) with prop as fallback
  const effectiveStripeAccountId = fetchedStripeAccountId || stripeAccountId;

  // Check if pending payment covers the full balance
  const effectivePaymentAmount = documentType === 'quote' ? depositAmount : balanceDue;
  const pendingCoversBalance = hasPendingPayment && pendingPaymentAmount >= effectivePaymentAmount;

  const handleDownloadPdf = async () => {
    if (!onDownloadPdf) return;

    setDownloadingPdf(true);
    try {
      const blob = await onDownloadPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFilename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setDownloadingPdf(false);
    }
  };
  const [applyCredit, setApplyCredit] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [error, setError] = useState('');
  const [showFullDocument, setShowFullDocument] = useState(false);
  const [signingModalOpen, setSigningModalOpen] = useState(false);
  const [signing, setSigning] = useState(false);

  // Review state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComments, setReviewComments] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  const isQuote = documentType === 'quote';
  const paymentAmount = isQuote ? depositAmount : balanceDue;
  const creditToApply = applyCredit ? Math.min(availableCredit, paymentAmount) : 0;
  const stripeAmount = paymentAmount - creditToApply;
  const canPayWithCreditOnly = creditToApply >= paymentAmount && availableCredit > 0;

  // Signature flow
  const needsSignature = requiresSignature && !signatureCollected;

  const handleSignClient = async (signatory_name: string, signature_image: string) => {
    if (!onSignComplete) return;
    setSigning(true);
    try {
      await onSignComplete(signatory_name, signature_image);
      setSigningModalOpen(false);
    } finally {
      setSigning(false);
    }
  };

  const handleApplyCreditOnly = async () => {
    setConfirming(true);
    setError('');

    const response = await publicClientApi.applyCreditOnly(clientId, {
      [isQuote ? 'quote_id' : 'invoice_id']: documentId,
      amount: creditToApply.toString(),
    });

    setConfirming(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    setPaymentComplete(true);
    // Only auto-redirect for quotes or when reviews are disabled
    if (isQuote || !clientReviewsEnabled) {
      setTimeout(() => {
        onPaymentSuccess();
      }, 1500);
    }
  };

  const handleCreatePaymentIntent = async () => {
    setCreatingIntent(true);
    setError('');

    const createIntent = isQuote
      ? publicClientApi.createQuotePaymentIntent
      : publicClientApi.createInvoicePaymentIntent;

    const response = await createIntent(clientId, documentId, {
      amount: stripeAmount.toString(),
      payment_method_type: selectedMethod,
      apply_credit: applyCredit ? creditToApply.toString() : undefined,
    });

    setCreatingIntent(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data?.client_secret) {
      setClientSecret(response.data.client_secret);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    setConfirming(true);
    setError('');

    const response = await publicClientApi.confirmPayment(clientId, {
      payment_intent_id: paymentIntentId,
      [isQuote ? 'quote_id' : 'invoice_id']: documentId,
      credit_applied: applyCredit ? creditToApply.toString() : '0',
    });

    setConfirming(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    // Check if this is a pending ACH payment
    if (response.data?.pending) {
      setPaymentPending(true);
      return;
    }

    setPaymentComplete(true);
    // Only auto-redirect for quotes or when reviews are disabled
    // For invoices with reviews enabled, user must interact (submit or skip)
    if (isQuote || !clientReviewsEnabled) {
      setTimeout(() => {
        onPaymentSuccess();
      }, 1500);
    }
  };

  // Handle ACH payments that go to processing state
  const handlePaymentProcessing = async (paymentIntentId: string) => {
    // For ACH payments, we still need to confirm on the backend
    // The backend will create a pending payment record
    await handlePaymentSuccess(paymentIntentId);
  };

  const handleSubmitReview = async () => {
    if (reviewRating === 0) return;

    setReviewSubmitting(true);
    const response = await publicDocumentApi.submitInvoiceReview(documentId, reviewRating, reviewComments);
    setReviewSubmitting(false);

    if (response.data) {
      setReviewSubmitted(true);
      setTimeout(() => {
        onPaymentSuccess();
      }, 1500);
    } else {
      // If review submission fails, just proceed without it
      onPaymentSuccess();
    }
  };

  const handleSkipReview = () => {
    onPaymentSuccess();
  };

  // Payment pending state (ACH submitted but awaiting bank clearance)
  if (paymentPending) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
            <Clock className="h-8 w-8 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payment submitted!</h1>
            <p className="text-gray-600 mt-1">
              Your bank payment is being processed. This typically takes 1-3 business days.
            </p>
            <p className="text-sm text-gray-500 mt-3">
              You'll receive a confirmation once the payment clears. You can safely close this page.
            </p>
          </div>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  // Payment complete state
  if (paymentComplete) {
    // Show review form for invoices if reviews are enabled and not yet submitted
    const showReviewForm = !isQuote && clientReviewsEnabled && !reviewSubmitted;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payment successful!</h1>
            <p className="text-gray-600 mt-1">
              {isQuote
                ? 'Thank you for accepting this estimate.'
                : 'Thank you for your payment.'}
            </p>
          </div>

          {showReviewForm && (
            <div className="bg-white rounded-lg p-6 border shadow-sm text-left space-y-4">
              <div className="text-center">
                <p className="text-lg font-medium text-gray-900">How was your experience?</p>
                <p className="text-sm text-gray-500">We'd love your feedback</p>
              </div>

              <div className="flex justify-center">
                <StarRating
                  value={reviewRating}
                  onChange={setReviewRating}
                  size="lg"
                />
              </div>

              {reviewRating > 0 && (
                <>
                  <Textarea
                    placeholder="Add a comment (optional)"
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                  <Button
                    onClick={handleSubmitReview}
                    disabled={reviewSubmitting}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {reviewSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Review'
                    )}
                  </Button>
                </>
              )}

              <button
                onClick={handleSkipReview}
                className="text-sm text-gray-500 hover:text-gray-700 w-full text-center"
              >
                Skip
              </button>
            </div>
          )}

          {reviewSubmitted && (
            <p className="text-green-600 font-medium">Thank you for your feedback!</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Mobile header */}
        <div className="lg:hidden border-b p-4">
          {business.logo && (
            <img
              src={business.logo}
              alt={business.name}
              className="h-8 w-auto object-contain"
            />
          )}
        </div>

        <div className="max-w-6xl mx-auto lg:grid lg:grid-cols-5 lg:gap-8 lg:p-8">
          {/* Left column - Payment form */}
          <div className="lg:col-span-3 p-4 pb-24 lg:p-8 lg:pb-8">
            <div className="max-w-lg mx-auto lg:mx-0 space-y-8">
              {/* Header */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {needsSignature
                    ? isQuote
                      ? 'Sign & pay deposit'
                      : 'Sign & pay this invoice'
                    : isQuote
                      ? 'Pay deposit to accept this estimate'
                      : 'Pay this invoice'}
                </h1>
                <div className="mt-2">
                  <p className="text-gray-600 font-medium">{business.name}</p>
                  {business.address && (
                    <p className="text-sm text-gray-500 whitespace-pre-line">{business.address}</p>
                  )}
                  {business.phone && (
                    <p className="text-sm text-gray-500">{business.phone}</p>
                  )}
                </div>
              </div>

              {/* Bill To */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Bill To</p>
                <p className="font-medium text-gray-900">{clientName}</p>
                {clientCompany && <p className="text-sm text-gray-600">{clientCompany}</p>}
                {clientAddress && (
                  <p className="text-sm text-gray-500 whitespace-pre-line mt-1">{clientAddress}</p>
                )}
              </div>

              {/* Amount */}
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                  {isQuote ? 'Deposit Amount' : 'Amount Due'}
                </p>
                <p className="text-4xl font-bold text-gray-900 mt-1">
                  {formatAmount(paymentAmount)}
                </p>
              </div>

              {/* Error message */}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Pending payment message - show when there's already a pending ACH payment */}
              {pendingCoversBalance && !loadingPaymentInfo ? (
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-900">Payment pending</p>
                        <p className="text-sm text-amber-700 mt-1">
                          A bank payment of {formatAmount(pendingPaymentAmount)} is being processed.
                          This typically takes 1-3 business days.
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 text-center">
                    You'll receive a confirmation once the payment clears. No additional payment is needed at this time.
                  </p>
                </div>
              ) : needsSignature ? (
                /* Signature step - show when signature required but not collected */
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                    <div className="flex items-start gap-3">
                      <PenLine className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-900">Signature required</p>
                        <p className="text-sm text-amber-700 mt-1">
                          {contractContent
                            ? 'Please review and sign the contract before proceeding with payment.'
                            : 'Please sign below before proceeding with payment.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => setSigningModalOpen(true)}
                    disabled={signing || declining}
                    className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                  >
                    {signing ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Signing...
                      </>
                    ) : (
                      <>
                        <PenLine className="mr-2 h-5 w-5" />
                        Sign & Continue to Payment
                      </>
                    )}
                  </Button>
                  {onDecline && (
                    <Button
                      variant="outline"
                      onClick={onDecline}
                      disabled={signing || declining}
                      className="w-full"
                    >
                      {declining ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Requesting changes...
                        </>
                      ) : (
                        <>
                          <ThumbsDown className="mr-2 h-4 w-4" />
                          Request Changes
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {/* Payment method toggle */}
                  {cardEnabled && achEnabled && !clientSecret && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-3 block">
                        Payment method
                      </Label>
                      <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
                        <button
                          type="button"
                          onClick={() => setSelectedMethod('card')}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-sm font-medium transition-colors ${
                            selectedMethod === 'card'
                              ? 'bg-white shadow text-gray-900'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <CreditCard className="h-4 w-4" />
                          Debit card
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedMethod('ach')}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-sm font-medium transition-colors ${
                            selectedMethod === 'ach'
                              ? 'bg-white shadow text-gray-900'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Building2 className="h-4 w-4" />
                          Bank account
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Credit application toggle */}
                  {availableCredit > 0 && !clientSecret && (
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                      <div>
                        <Label className="text-sm font-medium text-gray-900">
                          Apply available credit
                        </Label>
                        <p className="text-sm text-gray-600">
                          You have {formatAmount(availableCredit)} in credit
                        </p>
                      </div>
                      <Switch
                        checked={applyCredit}
                        onCheckedChange={setApplyCredit}
                      />
                    </div>
                  )}

                  {/* Payment summary when credit applied */}
                  {applyCredit && creditToApply > 0 && !clientSecret && (
                    <div className="p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          {isQuote ? 'Deposit' : 'Balance due'}
                        </span>
                        <span>{formatAmount(paymentAmount)}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Credit applied</span>
                        <span>-{formatAmount(creditToApply)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-semibold">
                        <span>Amount to pay</span>
                        <span>{formatAmount(stripeAmount)}</span>
                      </div>
                    </div>
                  )}

                  {/* Stripe Payment Form or Pay button */}
                  {clientSecret ? (
                    <StripePaymentForm
                      clientSecret={clientSecret}
                      stripeAccountId={effectiveStripeAccountId}
                      onSuccess={handlePaymentSuccess}
                      onProcessing={handlePaymentProcessing}
                      onError={setError}
                      amount={stripeAmount}
                    />
                  ) : canPayWithCreditOnly && applyCredit ? (
                    <Button
                      onClick={handleApplyCreditOnly}
                      disabled={confirming}
                      className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                    >
                      {confirming ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Lock className="mr-2 h-5 w-5" />
                          {isQuote ? 'Accept and apply credit' : 'Apply credit'}
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleCreatePaymentIntent}
                      disabled={creatingIntent}
                      className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                    >
                      {creatingIntent ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Preparing payment...
                        </>
                      ) : (
                        <>
                          <Lock className="mr-2 h-5 w-5" />
                          {isQuote
                            ? `Accept and pay ${formatAmount(stripeAmount)} deposit`
                            : `Pay ${formatAmount(stripeAmount)}`}
                        </>
                      )}
                    </Button>
                  )}

                  {/* Security note */}
                  <p className="text-xs text-gray-500 text-center">
                    <Lock className="inline h-3 w-3 mr-1" />
                    Payments are securely processed by Stripe
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Right column - Document summary (desktop only) */}
          <div className="hidden lg:block lg:col-span-2 p-8 bg-gray-50">
            <div className="sticky top-8">
              <DocumentSummary
                documentType={documentType}
                business={business}
                documentNumber={documentNumber}
                documentDate={documentDate}
                dueDate={dueDate}
                subtotal={subtotal}
                shippingFee={shippingFee}
                totalTax={totalTax}
                totalAmount={totalAmount}
                amountPaid={amountPaid}
                balanceDue={balanceDue}
                depositAmount={depositAmount}
                onViewDocument={() => setShowFullDocument(true)}
              />

              {/* Download PDF - prefer on-demand generation to ensure signatures are current */}
              {onDownloadPdf ? (
                <Button
                  variant="ghost"
                  className="w-full mt-4 text-gray-500"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                >
                  {downloadingPdf ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {downloadingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
              ) : pdfUrl ? (
                <Button
                  variant="ghost"
                  className="w-full mt-4 text-gray-500"
                  asChild
                >
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" download>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </a>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  className="w-full mt-4 text-gray-500"
                  disabled
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile document summary button */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setShowFullDocument(true)}
          >
            View {isQuote ? 'estimate' : 'invoice'} details
          </Button>
        </div>
      </div>

      {/* Full document overlay - same as edit preview */}
      <DocumentPreviewOverlay
        open={showFullDocument}
        onClose={() => setShowFullDocument(false)}
        onDownloadPdf={onDownloadPdf}
        pdfFilename={pdfFilename}
      >
        {fullDocumentContent}
      </DocumentPreviewOverlay>

      {/* Client Signature Modal */}
      {requiresSignature && (
        <ClientSignatureModal
          open={signingModalOpen}
          onClose={() => setSigningModalOpen(false)}
          onComplete={handleSignClient}
          contractContent={contractContent}
          businessName={business.name}
          defaultName={clientName}
        />
      )}
    </>
  );
}
